import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Briefcase, Award, Building2,
  Globe, Lock, Users, Bell, CheckCircle, Eye,
  FileText, Linkedin, Clock, Info, UserCheck, AlertTriangle
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import ProfileCard from '../../components/consultant/ProfileCard';

interface ProfileData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  company: string;
  title: string;
  country_region: string;
  city?: string;
  state?: string;
  experience_years: string;
  workday_modules: string;
  workday_modules_list?: string[];
  certifications: string;
  experience_text: string;
  linkedin_url: string;
  availability_status: string;
  availability?: string;
  hourly_rate: string;
  profile_complete: boolean;
  project_end_date?: string;
  resume_url?: string;
  photo_url?: string;
  photo_position_x?: number;
  photo_position_y?: number;
  profile_visibility?: string;
  show_contact_info?: boolean;
  allow_direct_messages?: boolean;
  allow_connection_requests?: boolean;
  show_in_community_search?: boolean;
  project_preferences_list?: string[];
}

export default function ViewProfile() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { theme, setTheme, setThemeTransient } = useTheme();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLoadingLogo, setIsLoadingLogo] = useState(true);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [connectionsCount, setConnectionsCount] = useState(0);
  const [followersCount, setFollowersCount] = useState(0);
  const [showProfileCard, setShowProfileCard] = useState(false);

  useEffect(() => {
    setThemeTransient('light');
  }, [setThemeTransient]);

  useEffect(() => {
    if (user) {
      fetchProfile();
      fetchCounts();
    }
    fetchLogo();
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`view-profile-${user.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'consultant_profiles',
        filter: `id=eq.${user.id}`,
      }, () => { fetchProfile(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id]);

  const fetchLogo = async () => {
    try {
      setIsLoadingLogo(true);
      const defaultLogo = 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=80&fit=crop&auto=format';
      setLogoUrl(defaultLogo);
      const { data, error } = await supabase.from('settings').select('value').eq('key', 'logo_url').maybeSingle();
      if (!error && data?.value) {
        const img = new Image();
        img.onload = () => { setLogoUrl(data.value); setIsLoadingLogo(false); };
        img.onerror = () => setIsLoadingLogo(false);
        img.src = data.value;
      } else {
        setIsLoadingLogo(false);
      }
    } catch {
      setIsLoadingLogo(false);
    }
  };

  const fetchProfile = async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('consultant_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();
      if (error) throw error;
      if (data) {
        setProfile(data);
        if (data.photo_url) {
          if (data.photo_url.startsWith('http')) {
            setPhotoUrl(data.photo_url);
          } else {
            const { data: urlData } = supabase.storage.from('profile-photos').getPublicUrl(data.photo_url);
            setPhotoUrl(urlData.publicUrl);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCounts = async () => {
    if (!user?.id) return;
    try {
      const [connResult, followResult] = await Promise.all([
        supabase
          .from('community_connections')
          .select('id', { count: 'exact', head: true })
          .or(`requester_id.eq.${user.id},addressee_id.eq.${user.id}`)
          .eq('status', 'accepted'),
        supabase
          .from('community_followers')
          .select('id', { count: 'exact', head: true })
          .eq('following_id', user.id),
      ]);
      if (connResult.count !== null) setConnectionsCount(connResult.count);
      if (followResult.count !== null) setFollowersCount(followResult.count);
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  };

  const isDark = theme === 'dark';

  const calculateCompleteness = () => {
    if (!profile) return 0;
    const fields = [
      profile.first_name,
      profile.last_name,
      profile.email,
      profile.title,
      profile.country_region,
      profile.experience_years,
      profile.workday_modules,
      profile.resume_url,
      profile.photo_url,
      profile.experience_text,
    ];
    const filled = fields.filter(f => f && f.toString().trim() !== '').length;
    return Math.round((filled / fields.length) * 100);
  };

  const completeness = calculateCompleteness();
  const isClientVisible = completeness >= 80;
  const isInterviewEligible = completeness >= 90;

  const availabilityConfig = {
    available: { label: 'Available', dot: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    on_project: { label: 'On Project', dot: 'bg-amber-500', badge: 'bg-amber-100 text-amber-700 border-amber-200' },
    unavailable: { label: 'Unavailable', dot: 'bg-red-500', badge: 'bg-red-100 text-red-700 border-red-200' },
  };

  const avail = availabilityConfig[(profile?.availability_status || 'available') as keyof typeof availabilityConfig];

  const modules = (profile?.workday_modules_list && profile.workday_modules_list.length > 0)
    ? profile.workday_modules_list
    : profile?.workday_modules
    ? profile.workday_modules.split(',').map((m) => m.trim()).filter(Boolean)
    : [];

  const certs = profile?.certifications
    ? profile.certifications.split(',').map((c) => c.trim()).filter(Boolean)
    : [];

  const visibilityInfo = {
    public: { icon: Globe, label: 'Public', desc: 'Anyone can find and view your profile', color: 'text-emerald-600' },
    private: { icon: Lock, label: 'Private', desc: 'Only you can see your profile', color: 'text-red-500' },
    network: { icon: Users, label: 'Network Only', desc: 'Only connections can see your profile', color: 'text-blue-600' },
  }[(profile?.profile_visibility || 'public') as string] || { icon: Globe, label: 'Public', desc: 'Anyone can find your profile', color: 'text-emerald-600' };

  const VisIcon = visibilityInfo.icon;

  const privacyItems = [
    {
      label: 'Profile Visibility',
      value: visibilityInfo.label,
      desc: visibilityInfo.desc,
      icon: VisIcon,
      color: visibilityInfo.color,
    },
    {
      label: 'Contact Info',
      value: profile?.show_contact_info ? 'Shown to others' : 'Hidden from others',
      desc: profile?.show_contact_info ? 'Email shown (phone is always private)' : 'Contact details are hidden',
      icon: profile?.show_contact_info ? Eye : Lock,
      color: profile?.show_contact_info ? 'text-emerald-600' : 'text-gray-500',
    },
    {
      label: 'Direct Messages',
      value: profile?.allow_direct_messages ? 'Allowed' : 'Disabled',
      desc: profile?.allow_direct_messages ? 'Connections can message you' : 'Nobody can message you',
      icon: profile?.allow_direct_messages ? Bell : Lock,
      color: profile?.allow_direct_messages ? 'text-emerald-600' : 'text-gray-500',
    },
    {
      label: 'Community Search',
      value: profile?.show_in_community_search !== false ? 'Discoverable' : 'Hidden',
      desc: profile?.show_in_community_search !== false ? 'You appear in community searches' : 'Hidden from community search',
      icon: profile?.show_in_community_search !== false ? Users : Lock,
      color: profile?.show_in_community_search !== false ? 'text-emerald-600' : 'text-gray-500',
    },
  ];

  const initials = `${profile?.first_name?.[0] || ''}${profile?.last_name?.[0] || ''}`.toUpperCase();

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-200 ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <nav className="fixed w-full z-50 bg-black backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="text-white -mt-4 -mb-4 cursor-default select-none">
              {isLoadingLogo ? (
                <div className="h-32 md:h-28 w-[200px] flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]" />
                </div>
              ) : logoUrl ? (
                <img src="/1.png" alt="Logo" className="h-40 w-auto object-contain" />
              ) : (
                <span className="text-2xl font-bold">Perplexity</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/consultant/profile')}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white/80 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Edit Profile
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-24">
        <div className="max-w-3xl mx-auto px-4 py-8">

          <div className="space-y-3 mb-6">
            <div className={`rounded-xl border px-5 py-4 flex items-start gap-3 ${
              isDark ? 'bg-[#40E0D0]/10 border-[#40E0D0]/30' : 'bg-[#40E0D0]/5 border-[#40E0D0]/40'
            }`}>
              <Info className="w-5 h-5 text-[#40E0D0] flex-shrink-0 mt-0.5" />
              <div>
                <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  This is how your profile appears to others
                </p>
                <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Your phone number is always private and never displayed. Contact visibility is controlled by your privacy settings.
                </p>
              </div>
            </div>

            <div className={`rounded-xl border px-5 py-4 ${
              isInterviewEligible
                ? isDark ? 'bg-emerald-900/20 border-emerald-700/40' : 'bg-emerald-50 border-emerald-200'
                : isClientVisible
                ? isDark ? 'bg-amber-900/20 border-amber-700/40' : 'bg-amber-50 border-amber-200'
                : isDark ? 'bg-red-900/20 border-red-700/40' : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start gap-3">
                {isInterviewEligible
                  ? <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  : <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isClientVisible ? 'text-amber-500' : 'text-red-500'}`} />
                }
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <p className={`text-sm font-semibold ${
                      isInterviewEligible
                        ? isDark ? 'text-emerald-300' : 'text-emerald-800'
                        : isClientVisible
                        ? isDark ? 'text-amber-300' : 'text-amber-800'
                        : isDark ? 'text-red-300' : 'text-red-800'
                    }`}>
                      Profile {completeness}% Complete
                    </p>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                      isInterviewEligible
                        ? 'bg-emerald-100 text-emerald-700'
                        : isClientVisible
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {completeness}%
                    </span>
                  </div>
                  <div className={`w-full rounded-full h-1.5 mb-2 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div
                      className={`h-1.5 rounded-full transition-all ${
                        isInterviewEligible ? 'bg-emerald-500' : isClientVisible ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${completeness}%` }}
                    />
                  </div>
                  {isInterviewEligible ? (
                    <p className={`text-sm ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
                      Your profile is complete and visible to our clients. Clients can request interviews with you.
                    </p>
                  ) : isClientVisible ? (
                    <p className={`text-sm ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                      Your profile is visible to clients. Complete to 90% or higher so clients can request interviews with you.
                    </p>
                  ) : (
                    <p className={`text-sm ${isDark ? 'text-red-400' : 'text-red-700'}`}>
                      Your profile needs more information before it's fully visible to clients. Complete to at least 80% to appear in client searches.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className={`rounded-2xl shadow-lg overflow-hidden border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="h-32 bg-gradient-to-r from-[#40E0D0] to-[#2bbfb0] relative">
              <div className="absolute -bottom-14 left-8">
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt={`${profile?.first_name} ${profile?.last_name}`}
                    className={`w-28 h-28 rounded-full object-cover border-4 shadow-lg ${isDark ? 'border-gray-800' : 'border-white'}`}
                    style={{
                      objectPosition: `${profile?.photo_position_x ?? 50}% ${profile?.photo_position_y ?? 50}%`
                    }}
                  />
                ) : (
                  <div className={`w-28 h-28 rounded-full border-4 shadow-lg bg-[#40E0D0] flex items-center justify-center ${isDark ? 'border-gray-800' : 'border-white'}`}>
                    <span className="text-white text-3xl font-bold">{initials || '?'}</span>
                  </div>
                )}
              </div>
              <div className="absolute top-4 right-4">
                <button
                  onClick={() => setShowProfileCard(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white text-sm font-semibold rounded-full backdrop-blur-sm border border-white/30 transition-all"
                >
                  <Eye className="w-4 h-4" />
                  Preview Profile Card
                </button>
              </div>
            </div>

            <div className="pt-20 px-8 pb-8">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {profile?.first_name} {profile?.last_name}
                  </h1>
                  {profile?.title && (
                    <p className={`text-base font-medium mt-0.5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {profile.title}
                    </p>
                  )}
                </div>
                <span className={`text-sm font-semibold px-3 py-1.5 rounded-full border flex items-center gap-2 ${avail.badge}`}>
                  <span className={`w-2 h-2 rounded-full ${avail.dot}`} />
                  {avail.label}
                </span>
              </div>

              <div className={`flex flex-wrap gap-x-5 gap-y-1.5 mt-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {profile?.company && (
                  <span className="flex items-center gap-1.5">
                    <Building2 className="w-4 h-4" />
                    {profile.company}
                  </span>
                )}
                {(profile?.city || profile?.state || profile?.country_region) && (
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" />
                    {[profile.city, profile.state].filter(Boolean).join(', ') || profile.country_region}
                  </span>
                )}
                {profile?.experience_years && (
                  <span className="flex items-center gap-1.5">
                    <Briefcase className="w-4 h-4" />
                    {profile.experience_years} years Workday experience
                  </span>
                )}
                {profile?.linkedin_url && (
                  <a
                    href={profile.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-blue-500 hover:text-blue-600 transition-colors"
                  >
                    <Linkedin className="w-4 h-4" />
                    LinkedIn
                  </a>
                )}
              </div>

              <div className={`flex items-center gap-6 mt-4 pt-4 border-t text-sm ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-100 text-gray-500'}`}>
                <span className="flex items-center gap-2">
                  <UserCheck className="w-4 h-4" />
                  <strong className={isDark ? 'text-white' : 'text-gray-900'}>{connectionsCount}</strong> connections
                </span>
                <span className="flex items-center gap-2">
                  <Bell className="w-4 h-4" />
                  <strong className={isDark ? 'text-white' : 'text-gray-900'}>{followersCount}</strong> followers
                </span>
                {profile?.profile_complete && (
                  <span className="flex items-center gap-2 text-emerald-600">
                    <CheckCircle className="w-4 h-4" />
                    Profile complete
                  </span>
                )}
              </div>

              {profile?.show_contact_info && (
                <div className={`mt-6 pt-5 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                  <h3 className={`text-sm font-semibold uppercase tracking-wide mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Contact Information
                  </h3>
                  <div className="space-y-2">
                    <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      <span className="text-[#40E0D0]">@</span>
                      {profile.email}
                    </div>
                  </div>
                  <p className={`text-xs mt-2 flex items-center gap-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                    <Lock className="w-3 h-3" />
                    Phone number is always kept private
                  </p>
                </div>
              )}

              {!profile?.show_contact_info && (
                <div className={`mt-6 pt-5 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                  <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    <Lock className="w-4 h-4" />
                    Contact information is hidden based on your privacy settings
                  </div>
                </div>
              )}

              {profile?.availability_status === 'available' && profile?.hourly_rate && (
                <div className={`mt-5 pt-5 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                  <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <Clock className="w-4 h-4 text-[#40E0D0]" />
                    <span className="font-medium">Hourly Rate:</span>
                    <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>${profile.hourly_rate}/hr</span>
                  </div>
                </div>
              )}

              {modules.length > 0 && (
                <div className={`mt-6 pt-5 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                  <h3 className={`text-sm font-semibold uppercase tracking-wide mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Workday Modules
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {modules.map((mod, i) => (
                      <span
                        key={i}
                        className={`text-sm px-3 py-1.5 rounded-full font-medium border ${
                          isDark
                            ? 'bg-[#40E0D0]/10 text-[#40E0D0] border-[#40E0D0]/30'
                            : 'bg-[#40E0D0]/10 text-[#1a9e90] border-[#40E0D0]/40'
                        }`}
                      >
                        {mod}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {certs.length > 0 && (
                <div className={`mt-6 pt-5 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                  <h3 className={`text-sm font-semibold uppercase tracking-wide mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Certifications
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {certs.map((cert, i) => (
                      <span
                        key={i}
                        className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full font-medium bg-amber-50 text-amber-700 border border-amber-200"
                      >
                        <Award className="w-3.5 h-3.5" />
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {profile?.experience_text && (
                <div className={`mt-6 pt-5 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                  <h3 className={`text-sm font-semibold uppercase tracking-wide mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Experience
                  </h3>
                  <p className={`text-sm leading-relaxed whitespace-pre-wrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {profile.experience_text}
                  </p>
                </div>
              )}

              {profile?.availability && (
                <div className={`mt-5 pt-5 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                  <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <Clock className="w-4 h-4 text-[#40E0D0]" />
                    <span className="font-medium">Availability:</span>
                    <span>{profile.availability}</span>
                  </div>
                </div>
              )}

              {profile?.project_preferences_list && profile.project_preferences_list.length > 0 && (
                <div className={`mt-6 pt-5 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                  <h3 className={`text-sm font-semibold uppercase tracking-wide mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Project Preferences
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.project_preferences_list.map((pref, i) => (
                      <span
                        key={i}
                        className={`text-sm px-3 py-1.5 rounded-full font-medium border ${
                          isDark
                            ? 'bg-blue-900/20 text-blue-300 border-blue-700/40'
                            : 'bg-blue-50 text-blue-700 border-blue-200'
                        }`}
                      >
                        {pref}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {profile?.resume_url && (
                <div className={`mt-6 pt-5 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                  <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    <FileText className="w-4 h-4 text-[#40E0D0]" />
                    Resume is on file and available to recruiters
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={`mt-6 rounded-2xl border p-6 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h2 className={`text-base font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Your Privacy Settings
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {privacyItems.map((item, i) => {
                const ItemIcon = item.icon;
                return (
                  <div
                    key={i}
                    className={`rounded-xl p-4 border ${isDark ? 'border-gray-700 bg-gray-700/40' : 'border-gray-100 bg-gray-50'}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <ItemIcon className={`w-4 h-4 ${item.color}`} />
                      <span className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {item.label}
                      </span>
                    </div>
                    <p className={`text-sm font-semibold ${item.color}`}>{item.value}</p>
                    <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{item.desc}</p>
                  </div>
                );
              })}
            </div>
            <p className={`mt-4 text-xs flex items-center gap-1.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
              <Info className="w-3.5 h-3.5" />
              Manage these settings in{' '}
              <button
                onClick={() => navigate('/consultant/settings')}
                className="text-[#40E0D0] hover:underline font-medium"
              >
                Account Settings
              </button>
            </p>
          </div>
        </div>
      </div>

      {showProfileCard && profile && (
        <ProfileCard
          profile={profile}
          photoUrl={photoUrl}
          connectionsCount={connectionsCount}
          followersCount={followersCount}
          onClose={() => setShowProfileCard(false)}
          theme={theme}
        />
      )}
    </div>
  );
}
